import { fetchCourse } from 'actionCreators/course';
import { setError } from 'actionCreators/error';
import { get } from 'services/api';
import { appPostMessage, getCdnUrl } from 'utils';

import { decreaseLoading, increaseLoading } from './loading';
import { requestCustomization } from './customization';

export const receiveCourseTemplate = (courseTemplate: any) => ({
  type: 'RECEIVE_COURSE_TEMPLATE',
  courseTemplate,
});

export const cleanCourseTemplate = () => ({
  type: 'CLEAN_COURSE_TEMPLATE',
});

const receiveInitialTemplate = (
  template: any,
  title: string,
  subdomain: string,
  login_page: any,
  support_external_url: string
) => ({
  type: 'RECEIVE_INITIAL_TEMPLATE',
  template,
  subdomain,
  title,
  login_page,
  support_external_url,
});

export const fetchCourseTemplate = (courseHash: string) => async (dispatch: Function, getState: Function) => {
  try {
    await appPostMessage('show-splascreen');
    dispatch(increaseLoading());

    await Promise.all([dispatch(fetchCourse(courseHash)), dispatch(requestCustomization(courseHash))]);

    const state = getState();
    const customization = state.customization.customizationData;
    const course = state.course;
    const data = {
      logo: (course || {}).logo,
      ...(customization || {}),
    };

    const template = await parseCustomizationsToTemplate(data);
    dispatch(
      receiveCourseTemplate({
        ...template,
        customization: data.customization,
        isFree: (course || {}).access_type === 2,
        hash: courseHash,
      })
    );
    appPostMessage('hide-splascreen');

    return template;
  } catch (error) {
    await dispatch(setError(error));
    throw error;
  } finally {
    dispatch(decreaseLoading());
  }
};

export const fetchInitialTemplate = () => async (dispatch: Function, getState: Function) => {
  try {
    const { data } = await get({ url: '/learner/customization' });
    const template = await parseCustomizationsToTemplate(data.data);
    const login_page = {
      logo_login: (data.data || {}).logo_login,
      login_background_image: (data.data || {}).login_background_image,
    };

    dispatch(
      receiveInitialTemplate(
        template,
        (data.data || {}).title,
        data.subdomain,
        login_page,
        (data.data || {}).support_external_url
      )
    );
    appPostMessage('hide-splascreen');
  } catch (error) {
    dispatch(receiveInitialTemplate(null, null, null, null, null));
    appPostMessage('hide-splascreen');
    throw error;
  }
};

async function parseCustomizationsToTemplate(customizations: any) {
  let theme = '';
  const {
    header_background_color,
    title_color,
    header_link_color,
    cover_background_color,
    image_cover,
    logo,
    logo_login,
    login_background_image,
    login_background_color,
    layout,
    course_description_color,
  } = customizations || ({} as any);

  if (header_background_color) {
    theme += `
      .header-root {
        background-color: ${header_background_color};
      }

      .breadcrumb-root {
        background-color: ${header_background_color};
      }

      ${
        ['#fff', '#ffffff', 'white'].includes(header_background_color)
          ? `
      .header-root .header-logo svg {
        fill: black;
      }

      .news-root .course-news,
      .header-root .help-icon,
      .header-root .header-version,
      .breadcrumb-root .breads .bread .bread-icon,
      .breadcrumb-root .breads .bread a,
      .breadcrumb-root .breads .bread label,
      .user-menu-root .user-photo.onlyText {
        color: black;
      }

      .user-menu-root .user-menu:before {
        border-top-color: black;
      }

      `
          : ''
      }
    `;
  }

  if (header_link_color) {
    theme += `

      .news-root .course-news,
      .header-root .help-icon,
      .header-root .header-version,
      .breadcrumb-root .breads .bread .bread-icon,
      .breadcrumb-root .breads .bread a,
      .breadcrumb-root .breads .bread label,
      .user-menu-root .user-photo.onlyText { color: ${header_link_color}; }

      .header-root .header-hamburger-menu svg,
      .header-root .news-root svg { fill: ${header_link_color}; }

      .user-menu-root .user-menu:before { border-top-color: ${header_link_color}; }
      .footer-root .footer-logo .footer-logo-powered { opacity: .5; }

      // .login-root .login-form .buttonSend .button { background-color: ${header_link_color}; }
      // .login-root .login-form .buttonSend .button span { color: ${login_background_color || header_link_color}; }
      // .login-root .login-form .login-link { color: ${header_link_color}; }

      // .login-root .login-form .button.outline-dark,
      // .login-root .login-form .button.outline-dark span,
      // .login-root .panel-footer p {
      //   color: ${header_link_color};
      //   border-color: ${header_link_color};
      // }

      //.login-root .footer-root .footer-logo .footer-logo-powered,
      // .login-root .login-form .button.outline-dark:hover { border-color: transparent; }

      .login-root .title { color: ${header_link_color}; }

      .course-banner-root .banner-content .course-name { color: ${header_link_color}; }
    `;
  }

  if (title_color) {
    theme += `
      .course-banner-root .banner-course h1.course-name {
        color: ${title_color};
      }
      div.template-white div.course-banner-root h1.course-name {
        color: ${title_color};
      }
      .course-classic-root div.course-banner-root .banner-info .course-name {
        color: ${title_color};
      }
    `;
  }

  if (cover_background_color && !image_cover) {
    theme += `
      .course-banner-root .banner-img {
        background-color: ${cover_background_color} !important;
      }
    `;
  }

  if (logo) {
    try {
      const imageUrl = getCdnUrl(logo);
      const isSmall = await validImage(imageUrl, 55);

      theme += `
        .header-logo {
          height: 40px;
          width: 150px;
          background-repeat: no-repeat;
          background-position: center left;
          background-image: url(${imageUrl});
          ${isSmall ? '' : 'background-size: contain;'},
        }
        .header-logo svg {
          display: none !important;
        }

      `;
    } catch (error) {
      console.warn(error);
    }
  }

  if (logo_login) {
    try {
      const imageUrl = getCdnUrl(logo_login);
      await validImage(imageUrl);

      theme += `
        .nutror-logo {

          background-position: center;
          height: 150px;
          width: 150px;
          background-repeat: no-repeat;
          background-size: contain;
          background-image: url(${imageUrl});
        }
        .nutror-logo svg {
          display: none;
        }
      `;
    } catch (error) {
      console.warn(error);
    }
  }

  if (login_background_image) {
    try {
      const imageUrl = getCdnUrl(login_background_image);

      await validImage(imageUrl);

      theme += `
        .login-root .login-background img {
          content:url(${imageUrl});
        }
      `;
    } catch (error) {
      console.warn(error);
    }
  }

  // if (login_background_color) {
  //   theme += `
  //     .login-root .login-form {
  //       background: ${login_background_color};
  //     }

  //     .login-root .login-form label {
  //       color: ${header_link_color};
  //     }
  //   `;
  // }

  if (course_description_color) {
    theme += `
      .course-banner-root .banner-course .course-description,
      .course-classic-root div.course-banner-root .course-description {
        color: ${course_description_color};
      }
    `;
  }

  const themeColorMeta = document.getElementsByName('theme-color')[0];
  const shareImageMeta = document.getElementsByName('share-image')[0];

  themeColorMeta.setAttribute('content', header_background_color);
  shareImageMeta.setAttribute('content', login_background_image);

  return {
    theme,
    layout,
  };
}

async function validImage(imageUrl: string, maxHeight?: number) {
  return new Promise((resolve, reject) => {
    let image = new Image();

    image.onload = () => {
      resolve(!maxHeight ? true : image.height <= maxHeight);
    };
    image.onerror = reject;
    image.src = imageUrl;
  });
}
